import { type FC, useMemo, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel } from 'swiper/modules';
import { useUnit } from 'effector-react';

import 'swiper/css';
import styles from './HistoricalScenesSlider.module.css';
import { HistoricalSceneCard } from './HistoricalSceneCard';
import { useI18n } from '../../i18n';
import { $locale } from '../../state/locale';
import {
  resolveHistoricalFamily,
  navigateToHistoricalScene,
  closeHistory,
} from '../../state/historicalScenes';
import { isStubEraScene } from '../../scene/eraScenes/registry';
import {
  historicalSceneSelected,
  historicalSliderSlideChanged,
} from '../../analytics/historicalScenesTracker';

type Props = {
  /** The scene the slider is opened from (live scene or one of its eras). */
  activeSceneId: string;
};

/**
 * Historical-scenes slider — a centered era carousel over the live scene.
 * Mobile + desktop share this markup (desktop polish is a follow-up, see
 * docs/architecture/scene-registry.md). The overlay wrapper is pointer-transparent so the scene above
 * and the chat panel below stay interactive; only the card strip captures
 * input. The slider centers on the current scene's card and disables that
 * card's "Explore" (you are already there). RTL mirrors via Swiper `dir`.
 */
export const HistoricalScenesSlider: FC<Props> = ({ activeSceneId }) => {
  const i18n = useI18n();
  const locale = useUnit($locale);
  const isRtl = locale === 'ar';

  const family = resolveHistoricalFamily(activeSceneId);

  const currentIndex = useMemo(() => {
    if (!family) return 0;
    const idx = family.slides.findIndex((slide) => slide.sceneId === activeSceneId);
    return idx >= 0 ? idx : 0;
  }, [family, activeSceneId]);

  // Double-tap guard: navigation itself no-ops on the second call (the store
  // updates synchronously), but the analytics dispatch would fire twice.
  // No reset needed — the slider unmounts on navigation ($historyMode reset).
  const exploredRef = useRef(false);

  if (!family) return null;

  const handleExplore = (sceneId: string) => {
    // Locked cards render no CTA; guard anyway so the only way into an era is
    // one that exists (the menu tab reaches `navigateToHistoricalScene` too).
    if (isStubEraScene(sceneId)) return;
    if (exploredRef.current) return;
    exploredRef.current = true;
    historicalSceneSelected({ fromSceneId: activeSceneId, toSceneId: sceneId });
    // Closes the slider via `$historyMode` reset on navigation.
    navigateToHistoricalScene(sceneId);
  };

  return (
    <div
      className={styles.overlay}
      // Modal scrim: tapping outside the card strip dismisses the slider.
      // Guarded by target===currentTarget so card / swipe clicks don't close it.
      onClick={(e) => {
        if (e.target === e.currentTarget) closeHistory();
      }}
    >
      <Swiper
        // Re-init Swiper when direction flips so RTL centering recomputes.
        key={isRtl ? 'rtl' : 'ltr'}
        dir={isRtl ? 'rtl' : 'ltr'}
        modules={[Mousewheel]}
        slidesPerView="auto"
        centeredSlides
        spaceBetween={12}
        initialSlide={currentIndex}
        slideToClickedSlide
        threshold={5}
        // Track which era card the user browses to (swipe / wheel / clicked
        // side card). Does NOT fire for `initialSlide`, so it counts only
        // user-driven views beyond the card they opened on.
        onSlideChange={(swiper) => {
          const slide = family.slides[swiper.activeIndex];
          if (slide) {
            historicalSliderSlideChanged({
              toSceneId: slide.sceneId,
              index: swiper.activeIndex,
            });
          }
        }}
        // Desktop: a vertical mouse wheel (deltaY) scrolls the carousel
        // horizontally. `forceToAxis: false` maps any wheel axis to the slider;
        // `releaseOnEdges: false` keeps the wheel captured by the modal (no page
        // scroll leak). No-op on touch devices (no wheel events).
        mousewheel={{ forceToAxis: false, releaseOnEdges: false }}
        className={styles.swiper}
      >
        {family.slides.map((slide) => {
          const label = i18n.historicalScenes.eras[slide.labelKey];
          return (
            <SwiperSlide key={slide.sceneId} className={styles.slide}>
              <HistoricalSceneCard
                title={label?.title ?? slide.labelKey}
                subtitle={label?.subtitle ?? ''}
                preview={slide.preview}
                exploreLabel={i18n.historicalScenes.exploreCta}
                comingSoonLabel={i18n.historicalScenes.comingSoon}
                isCurrent={slide.sceneId === activeSceneId}
                isLocked={isStubEraScene(slide.sceneId)}
                onExplore={() => handleExplore(slide.sceneId)}
              />
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};
